<template>
  <Head :title="dj_name" />

  <div class="min-h-screen bg-gradient-to-br from-black via-purple-950 to-black text-white overflow-hidden">
    <!-- Animated background -->
    <div class="fixed inset-0 overflow-hidden pointer-events-none">
      <div class="absolute -top-1/2 -left-1/2 w-full h-full bg-gradient-to-br from-fuchsia-600/20 to-transparent rounded-full blur-3xl animate-pulse-slow"></div>
      <div class="absolute -bottom-1/2 -right-1/2 w-full h-full bg-gradient-to-tl from-cyan-600/20 to-transparent rounded-full blur-3xl animate-pulse-slow-delayed"></div>
      <div class="absolute top-1/4 right-1/4 w-96 h-96 bg-purple-600/10 rounded-full blur-3xl animate-float"></div>
    </div>

    <!-- Mobile Menu Overlay -->
    <Transition name="fade">
      <div v-if="mobileMenuOpen" class="fixed inset-0 z-40 bg-black/90 backdrop-blur-sm md:hidden" @click="mobileMenuOpen = false"></div>
    </Transition>

    <!-- Mobile Menu -->
    <Transition name="slide">
      <div v-if="mobileMenuOpen" class=" fixed top-0 right-0 z-50 h-full w-72 bg-gradient-to-br from-purple-950 to-black border-l border-white/10 p-6 md:hidden">
        <div class="flex justify-end mb-8">
          <button @click="mobileMenuOpen = false" class="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center hover:border-fuchsia-400 transition-colors">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>
        <nav class="flex flex-col gap-6 text-lg">
          <a href="#about" @click="mobileMenuOpen = false" class="hover:text-fuchsia-400 transition-colors py-2 border-b border-white/10">{{ t('nav.about') }}</a>
          <a href="#style" @click="mobileMenuOpen = false" class="hover:text-fuchsia-400 transition-colors py-2 border-b border-white/10">{{ t('nav.music') }}</a>
          <a href="#contact" @click="mobileMenuOpen = false" class="hover:text-fuchsia-400 transition-colors py-2 border-b border-white/10">{{ t('nav.contact') }}</a>
        </nav>
        <!-- Language Switcher - Mobile -->
        <div class="flex items-center justify-center gap-2 mt-10">
          <button @click="switchLanguage('en-US'); mobileMenuOpen = false"
                  :class="locale === 'en-US' ? 'bg-fuchsia-600 text-white border-fuchsia-600' : 'bg-transparent text-white/70 border-white/30 hover:border-white/50'"
                  class="flex-1 py-3 border rounded-lg font-semibold tracking-wider transition-all">
            EN
          </button>
          <button @click="switchLanguage('pt-BR'); mobileMenuOpen = false"
                  :class="locale === 'pt-BR' ? 'bg-fuchsia-600 text-white border-fuchsia-600' : 'bg-transparent text-white/70 border-white/30 hover:border-white/50'"
                  class="flex-1 py-3 border rounded-lg font-semibold tracking-wider transition-all">
            PT
          </button>
        </div>
        <div class="flex items-center justify-center gap-3 mt-6">
          <a v-if="social_links.instagram" :href="social_links.instagram" target="_blank"
             class="flex-shrink-0 w-10 h-10 rounded-full border border-white/20 flex items-center justify-center hover:border-fuchsia-400 hover:text-fuchsia-400 transition-all">
            <IconInstagram class="w-5 h-5 flex-shrink-0" />
          </a>
          <a v-if="social_links.spotify" :href="social_links.spotify" target="_blank"
             class="flex-shrink-0 w-10 h-10 rounded-full border border-white/20 flex items-center justify-center hover:border-green-400 hover:text-green-400 transition-all">
            <IconSpotify class="w-5 h-5 flex-shrink-0" />
          </a>
        </div>
      </div>
    </Transition>

    <!-- Navigation -->
    <nav class="relative z-10 flex items-center justify-between px-4 md:px-12 py-4 md:py-6">
      <div class="text-xl md:text-2xl font-bold tracking-wider bg-gradient-to-r from-fuchsia-400 to-cyan-400 bg-clip-text text-transparent">
        {{ dj_name }}
      </div>
      <div class="hidden md:flex items-center gap-8 text-sm tracking-wide">
        <a href="#about" class="hover:text-fuchsia-400 transition-colors">{{ t('nav.about') }}</a>
        <a href="#style" class="hover:text-fuchsia-400 transition-colors">{{ t('nav.music') }}</a>
        <a href="#contact" class="hover:text-fuchsia-400 transition-colors">{{ t('nav.contact') }}</a>
      </div>
      <div class="flex items-center gap-2 md:gap-4">
        <!-- Language Switcher - Desktop -->
        <div class="hidden md:flex items-center gap-1 text-xs font-medium tracking-wider mr-4">
          <button @click="switchLanguage('en-US')" :class="locale === 'en-US' ? 'text-fuchsia-400' : 'text-white/50 hover:text-white'" class="px-2 py-1 transition-colors">EN</button>
          <span class="text-white/30">|</span>
          <button @click="switchLanguage('pt-BR')" :class="locale === 'pt-BR' ? 'text-fuchsia-400' : 'text-white/50 hover:text-white'" class="px-2 py-1 transition-colors">PT</button>
        </div>
        <!-- Desktop Social Icons -->
        <div class="hidden md:flex items-center gap-2 lg:gap-3">
          <a v-if="social_links.instagram" :href="social_links.instagram" target="_blank"
             class="flex-shrink-0 w-10 h-10 rounded-full border border-white/20 flex items-center justify-center hover:border-fuchsia-400 hover:text-fuchsia-400 transition-all hover:scale-110">
            <IconInstagram class="w-5 h-5 flex-shrink-0" />
          </a>
          <a v-if="social_links.spotify" :href="social_links.spotify" target="_blank"
             class="flex-shrink-0 w-10 h-10 rounded-full border border-white/20 flex items-center justify-center hover:border-green-400 hover:text-green-400 transition-all hover:scale-110">
            <IconSpotify class="w-5 h-5 flex-shrink-0" />
          </a>
        </div>
        <!-- Mobile Menu Button -->
        <button @click="mobileMenuOpen = !mobileMenuOpen" class="md:hidden w-10 h-10 rounded-full border border-white/20 flex items-center justify-center hover:border-fuchsia-400 transition-colors">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16"></path>
          </svg>
        </button>
      </div>
    </nav>

    <!-- Hero Section -->
    <section class="relative z-10 h-[calc(100vh-72px)] md:h-[calc(100vh-88px)] flex flex-col items-center justify-center text-center px-4 md:px-6">
      <div class="relative">
        <!-- Glow effect behind DJ name -->
        <div class="absolute inset-0 blur-3xl bg-gradient-to-r from-fuchsia-600/40 to-cyan-600/40 scale-150"></div>

        <h1 class="relative text-6xl sm:text-5xl md:text-7xl lg:text-8xl font-black tracking-tighter mb-4">
          <span class="bg-gradient-to-r from-white via-fuchsia-200 to-cyan-200 bg-clip-text text-transparent drop-shadow-2xl">
            {{ dj_name }}
          </span>
        </h1>
      </div>

      <p class="text-base md:text-xl lg:text-2xl text-white/70 tracking-widest uppercase mb-8 md:mb-12 max-w-2xl">
        {{ t('hero.tagline') }}
      </p>

      <div class="flex flex-col sm:flex-row gap-3 md:gap-4">
        <a href="#contact" class="group px-6 md:px-8 py-3 md:py-4 bg-gradient-to-r from-fuchsia-600 to-purple-600 rounded-full font-semibold tracking-wide text-sm md:text-base hover:from-fuchsia-500 hover:to-purple-500 transition-all hover:scale-105 hover:shadow-lg hover:shadow-fuchsia-600/25">
          {{ t('hero.bookNow') }}
          <span class="inline-block ml-2 group-hover:translate-x-1 transition-transform">&rarr;</span>
        </a>
        <a href="https://wa.me/5521999289308" target="_blank" class="px-6 md:px-8 py-3 md:py-4 border border-white/30 rounded-full font-semibold tracking-wide text-sm md:text-base hover:bg-white/10 transition-all hover:scale-105 flex items-center justify-center gap-2">
          <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
          </svg>
          WhatsApp
        </a>
      </div>

      <!-- Scroll indicator -->
      <div class="absolute bottom-8 animate-bounce">
        <svg class="w-6 h-6 text-white/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 14l-7 7m0 0l-7-7m7 7V3"></path>
        </svg>
      </div>
    </section>

    <!-- About Section -->
    <section id="about" class="relative z-10 py-16 md:py-24 px-4 md:px-12">
      <div class="max-w-6xl mx-auto grid md:grid-cols-2 gap-8 md:gap-12 items-center">
        <div class="relative order-2 md:order-1">
          <div class="aspect-square rounded-3xl bg-gradient-to-br from-fuchsia-600/20 to-cyan-600/20 border border-white/10 overflow-hidden">
            <img :src="clauh1" alt="CLAUH.DJ" class="w-full h-full object-cover" />
            <div class="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
          </div>
        </div>
        <div class="order-1 md:order-2">
          <h2 class="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 md:mb-6 bg-gradient-to-r from-fuchsia-400 to-cyan-400 bg-clip-text text-transparent">
            {{ t('about.title') }}
          </h2>
          <p class="text-base md:text-lg text-white/70 leading-relaxed mb-4 md:mb-6">
            {{ t('about.paragraph1') }}
          </p>
          <p class="text-base md:text-lg text-white/70 leading-relaxed mb-6 md:mb-8">
            {{ t('about.paragraph2') }}
          </p>
          <!-- Features -->
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div class="p-4 rounded-xl bg-white/5 border border-white/10">
              <div class="text-fuchsia-400 font-semibold mb-1">{{ t('about.features.curation') }}</div>
              <div class="text-white/50 text-sm">{{ t('about.features.curationDesc') }}</div>
            </div>
            <div class="p-4 rounded-xl bg-white/5 border border-white/10">
              <div class="text-cyan-400 font-semibold mb-1">{{ t('about.features.collaboration') }}</div>
              <div class="text-white/50 text-sm">{{ t('about.features.collaborationDesc') }}</div>
            </div>
            <div class="p-4 rounded-xl bg-white/5 border border-white/10">
              <div class="text-purple-400 font-semibold mb-1">{{ t('about.features.respect') }}</div>
              <div class="text-white/50 text-sm">{{ t('about.features.respectDesc') }}</div>
            </div>
            <div class="p-4 rounded-xl bg-white/5 border border-white/10">
              <div class="text-pink-400 font-semibold mb-1">{{ t('about.features.professionalism') }}</div>
              <div class="text-white/50 text-sm">{{ t('about.features.professionalismDesc') }}</div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Music Style Section -->
    <section id="style" class="relative z-10 py-16 md:py-24 px-4 md:px-12 bg-gradient-to-b from-transparent via-purple-950/50 to-transparent">
      <div class="max-w-4xl mx-auto text-center">
        <h2 class="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 md:mb-6 bg-gradient-to-r from-fuchsia-400 to-cyan-400 bg-clip-text text-transparent">
          {{ t('music.title') }}
        </h2>
        <p class="text-white/50 mb-8 md:mb-12 text-sm md:text-base">{{ t('music.subtitle') }}</p>

        <div class="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          <div class="p-6 rounded-xl bg-white/5 border border-white/10 hover:border-fuchsia-500/50 transition-all">
            <div class="text-3xl mb-2">🔥</div>
            <div class="text-white/70 text-sm">{{ t('music.genres.hits') }}</div>
          </div>
          <div class="p-6 rounded-xl bg-white/5 border border-white/10 hover:border-fuchsia-500/50 transition-all">
            <div class="text-3xl mb-2">🎵</div>
            <div class="text-white/70 text-sm">{{ t('music.genres.world') }}</div>
          </div>
          <div class="p-6 rounded-xl bg-white/5 border border-white/10 hover:border-fuchsia-500/50 transition-all">
            <div class="text-3xl mb-2">🎸</div>
            <div class="text-white/70 text-sm">{{ t('music.genres.rock') }}</div>
          </div>
          <div class="p-6 rounded-xl bg-white/5 border border-white/10 hover:border-fuchsia-500/50 transition-all">
            <div class="text-3xl mb-2">🎧</div>
            <div class="text-white/70 text-sm">{{ t('music.genres.dance') }}</div>
          </div>
        </div>

        <p class="mt-8 text-white/50 text-sm md:text-base italic">
          "{{ t('music.quote') }}"
        </p>
      </div>
    </section>

    <!-- Gallery Section -->
    <section class="relative z-10 py-16 md:py-24 px-4 md:px-12">
      <div class="max-w-6xl mx-auto">
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
          <div class="aspect-square rounded-2xl bg-gradient-to-br from-fuchsia-600/20 to-cyan-600/20 border border-white/10 overflow-hidden hover:scale-105 transition-transform duration-300">
            <img :src="clauh2" alt="CLAUH.DJ" class="w-full h-full object-cover" />
          </div>
          <div class="aspect-[2/1] md:aspect-auto md:col-span-2 rounded-2xl bg-gradient-to-br from-fuchsia-600/20 to-cyan-600/20 border border-white/10 overflow-hidden hover:scale-[1.02] transition-transform duration-300">
            <img :src="clauh3" alt="CLAUH.DJ" class="w-full h-full object-cover" />
          </div>
        </div>
      </div>
    </section>

    <!-- Contact Section -->
    <section id="contact" class="relative z-10 py-16 md:py-24 px-4 md:px-12 bg-gradient-to-b from-transparent via-purple-950/50 to-black">
      <div class="max-w-2xl mx-auto text-center">
        <h2 class="text-3xl md:text-4xl lg:text-5xl font-bold mb-2 md:mb-4 bg-gradient-to-r from-fuchsia-400 to-cyan-400 bg-clip-text text-transparent">
          {{ t('contact.title') }}
        </h2>
        <p class="text-white/50 mb-8 md:mb-12 text-sm md:text-base">{{ t('contact.subtitle') }}</p>

        <form action="/bookings" method="post" class="space-y-4 md:space-y-6" @submit.prevent="submitForm">
          <input type="hidden" name="authenticity_token" :value="csrfToken" />
          <div class="grid md:grid-cols-2 gap-4 md:gap-6">
            <input type="text" name="booking[name]" :placeholder="t('contact.namePlaceholder')" v-model="form.name" required
                   class="w-full px-4 md:px-6 py-3 md:py-4 rounded-xl bg-white/5 border border-white/10 focus:border-fuchsia-500 focus:outline-none focus:ring-2 focus:ring-fuchsia-500/20 transition-all placeholder-white/30 text-sm md:text-base">
            <input type="email" name="booking[email]" :placeholder="t('contact.emailPlaceholder')" v-model="form.email" required
                   class="w-full px-4 md:px-6 py-3 md:py-4 rounded-xl bg-white/5 border border-white/10 focus:border-fuchsia-500 focus:outline-none focus:ring-2 focus:ring-fuchsia-500/20 transition-all placeholder-white/30 text-sm md:text-base">
          </div>
          <div class="grid md:grid-cols-2 gap-4 md:gap-6">
            <input type="tel" name="booking[phone]" :placeholder="t('contact.phonePlaceholder')" v-model="form.phone"
                   class="w-full px-4 md:px-6 py-3 md:py-4 rounded-xl bg-white/5 border border-white/10 focus:border-fuchsia-500 focus:outline-none focus:ring-2 focus:ring-fuchsia-500/20 transition-all placeholder-white/30 text-sm md:text-base">
            <input type="text" name="booking[event_location]" :placeholder="t('contact.locationPlaceholder')" v-model="form.event_location" required
                   class="w-full px-4 md:px-6 py-3 md:py-4 rounded-xl bg-white/5 border border-white/10 focus:border-fuchsia-500 focus:outline-none focus:ring-2 focus:ring-fuchsia-500/20 transition-all placeholder-white/30 text-sm md:text-base">
          </div>
          <input type="datetime-local" name="booking[event_date]" v-model="form.event_date" required
                 class="w-full px-4 md:px-6 py-3 md:py-4 rounded-xl bg-white/5 border border-white/10 focus:border-fuchsia-500 focus:outline-none focus:ring-2 focus:ring-fuchsia-500/20 transition-all placeholder-white/30 text-sm md:text-base">
          <textarea name="booking[message]" :placeholder="t('contact.messagePlaceholder')" rows="4" v-model="form.message"
                    class="w-full px-4 md:px-6 py-3 md:py-4 rounded-xl bg-white/5 border border-white/10 focus:border-fuchsia-500 focus:outline-none focus:ring-2 focus:ring-fuchsia-500/20 transition-all placeholder-white/30 resize-none text-sm md:text-base"></textarea>
          <div v-if="successMessage" class="p-4 rounded-xl bg-green-500/20 border border-green-500/30 text-green-400 text-sm">
            {{ successMessage }}
          </div>
          <div v-if="errorMessage" class="p-4 rounded-xl bg-red-500/20 border border-red-500/30 text-red-400 text-sm">
            {{ errorMessage }}
          </div>
          <button type="submit"
                  class="w-full md:w-auto px-8 md:px-12 py-3 md:py-4 bg-gradient-to-r from-fuchsia-600 to-cyan-600 rounded-xl font-semibold tracking-wide text-sm md:text-base hover:from-fuchsia-500 hover:to-cyan-500 transition-all hover:scale-105 hover:shadow-lg hover:shadow-fuchsia-600/25">
            {{ t('contact.sendButton') }}
          </button>
        </form>

        <!-- Direct contact -->
        <div class="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4 text-white/50">
          <a :href="`tel:${phone}`" class="flex items-center gap-2 hover:text-fuchsia-400 transition-colors">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path>
            </svg>
            {{ phone }}
          </a>
          <span class="hidden sm:inline">|</span>
          <a :href="`https://wa.me/5521999289308`" target="_blank" class="flex items-center gap-2 hover:text-green-400 transition-colors">
            <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
            WhatsApp
          </a>
        </div>
      </div>
    </section>

    <!-- Footer -->
    <footer class="relative z-10 py-6 md:py-8 px-4 md:px-6 border-t border-white/10">
      <div class="max-w-6xl mx-auto flex flex-col items-center gap-4 text-center">
        <div class="text-xl md:text-2xl font-bold tracking-wider bg-gradient-to-r from-fuchsia-400 to-cyan-400 bg-clip-text text-transparent">
          {{ dj_name }}
        </div>
        <p class="text-white/50 text-sm italic max-w-md">
          "{{ t('footer.tagline') }}"
        </p>
        <div class="flex items-center gap-4">
          <a v-if="social_links.instagram" :href="social_links.instagram" target="_blank"
             class="text-white/50 hover:text-fuchsia-400 transition-colors flex-shrink-0">
            <IconInstagram class="w-5 h-5 flex-shrink-0" />
          </a>
          <a v-if="social_links.spotify" :href="social_links.spotify" target="_blank"
             class="text-white/50 hover:text-green-400 transition-colors flex-shrink-0">
            <IconSpotify class="w-5 h-5 flex-shrink-0" />
          </a>
        </div>
        <div class="text-white/30 text-xs md:text-sm">
          &copy; {{ new Date().getFullYear() }} {{ dj_name }}. {{ t('footer.rights') }}
        </div>
        <div class="text-white/40 text-xs md:text-sm mt-2 pt-3 border-t border-white/10">
          {{ t('footer.devBy') }} <a href="https://pvpmartins.dev" target="_blank" class="font-semibold bg-gradient-to-r from-fuchsia-400 to-cyan-400 bg-clip-text text-transparent hover:from-fuchsia-300 hover:to-cyan-300 transition-all">Paulo Vitor Martins</a>
        </div>
      </div>
    </footer>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { Head, router } from '@inertiajs/vue3'
import { useI18n, initI18n } from '@/i18n'
import IconInstagram from '@/components/icons/IconInstagram.vue'
import IconSpotify from '@/components/icons/IconSpotify.vue'
import clauh1 from '@/assets/images/clauh1.png'
import clauh2 from '@/assets/images/clauh2.png'
import clauh3 from '@/assets/images/clauh3.png'
import clauh4 from '@/assets/images/clauh4.png'

interface SocialLinks {
  instagram?: string
  spotify?: string
}

defineProps<{
  dj_name: string
  phone: string
  social_links: SocialLinks
}>()

const { t, locale, setLocale } = useI18n()
const mobileMenuOpen = ref(false)
const successMessage = ref('')
const errorMessage = ref('')

const form = ref({
  name: '',
  email: '',
  phone: '',
  event_location: '',
  event_date: '',
  message: ''
})

const csrfToken = computed(() => {
  const meta = document.querySelector('meta[name="csrf-token"]')
  return meta ? meta.getAttribute('content') || '' : ''
})

onMounted(() => {
  initI18n()
})

function switchLanguage(lang: 'en-US' | 'pt-BR') {
  setLocale(lang)
}

function submitForm() {
  successMessage.value = ''
  errorMessage.value = ''

  router.post('/bookings', { booking: form.value }, {
    onSuccess: () => {
      successMessage.value = t('contact.successMessage')
      form.value = {
        name: '',
        email: '',
        phone: '',
        event_location: '',
        event_date: '',
        message: ''
      }
    },
    onError: () => {
      errorMessage.value = t('contact.errorMessage')
    }
  })
}
</script>

<style>
@keyframes pulse-slow {
  0%, 100% { opacity: 0.4; }
  50% { opacity: 0.7; }
}

@keyframes float {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-20px); }
}

@keyframes spin-slow {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.animate-pulse-slow {
  animation: pulse-slow 4s ease-in-out infinite;
}

.animate-pulse-slow-delayed {
  animation: pulse-slow 4s ease-in-out infinite;
  animation-delay: 2s;
}

.animate-float {
  animation: float 6s ease-in-out infinite;
}

.animate-spin-slow {
  animation: spin-slow 8s linear infinite;
}

/* Mobile menu transitions */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.slide-enter-active,
.slide-leave-active {
  transition: transform 0.3s ease;
}

.slide-enter-from,
.slide-leave-to {
  transform: translateX(100%);
}
</style>
